import Button from "../../models/Button";
import ButtonChannel from "../../models/ButtonChannel";
import ConnectableObject from "../../models/ConnectableObject";
import Connection, { CreateConnectionResult } from "../../models/Connection";
import Multiplexer from "../../models/Multiplexer";
import EditableText from "../EditableText";
import ObjectReference from "../ObjectReference";
import { PanelCell, PanelCells } from "./PanelLayoutTypes";
import PanelUtils from "./PanelUtils";
import Consumer from "../../models/Consumer";
import Scene from "../../models/Scene";
import { showErrToast } from "../Toasts";
import { ChannelType, getChannelTypePicture } from "../../models/ChannelType";
import { itx } from "../../translationItx/itx";
import { VariantType } from "react-tooltip";
import { isUiModeExpert } from "../../uiMode";
import { faCog, faGear } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState } from "react";
import ExtProperties from "./ExtPropeties";
import { PanelConfig } from "./PanelConfig";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import CurrentDrawingConnection from "../../CurrentDrawingConnection";

let dragStartCell: PanelCell | undefined;

export default function OnePanelCell(props: {
  panel: Button;
  redraw: () => void;
  panelCells: PanelCells;
  panelMappingConfig: PanelConfig;

  cell: PanelCell | undefined;
  idx: string | undefined;
  row: number;
  column: number;
  width: number;
  height: number;
  pageIndex: number;
}) {
  const { panel, redraw, panelCells, panelMappingConfig, cell, idx, row, column, pageIndex, width, height } = props;
  const [extPropertiesOpen, setExtPropertiesOpen] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  let functionChannel = cell?.functionTag ? panel.buttonChannels.getItemByTag(cell?.functionTag) : undefined;

  useOnChangeWatcher(functionChannel);

  const connectedObject = cell?.connectedObjectID
    ? (state.objectRegistry.getObject(cell.connectedObjectID) as ConnectableObject)
    : undefined;

  //   let connection = connectedObject?.connections.findFirst((connection: Connection) => {
  //     const partner = connection.getPartner(connectedObject);
  //     if (partner?.parent == panel) {
  //       return true;
  //     }
  //     return false;
  //   });

  function getFunctionChannel(obj: ConnectableObject): ButtonChannel {
    let functionChannel1 = obj.getConnectedButtonChannels().find((channel) => channel.parent == panel);

    if (functionChannel1) {
      //
      // obj is already connected to a function of the panel
      //
      return functionChannel1;
    }

    //
    // search a buttonChannel that is not connected to any other object
    //

    const freeFunctions = panel.buttonChannels.sortBy("tag").filter(
      (buttonChannel) =>
        /^T\d/.test(buttonChannel.tag) && // nur Tags der Form "T gefolgt von einer Zahl"
        buttonChannel.connections.size == 0,
    );

    if (freeFunctions.length > 0) {
      return freeFunctions[0];
    }

    //
    // create a new buttonChannel
    // this buttonChannel will have no implementation/knxChannel yet
    // the user will have to increase the number of functions later on
    //

    //
    // Wenn der Consumer noch nicht mit einem Button / Funktion des Tasters verbunden ist
    // dann wird automatisch eine neue Funktion erstellt, die mit dem Consumer verbunden wird
    //

    let maxTagIndex = 0;
    panel.buttonChannels.forEach((channel: ButtonChannel) => {
      const tagIndex = parseInt(channel.tag.replace("T", ""));
      if (tagIndex > maxTagIndex) {
        maxTagIndex = tagIndex;
      }
    });

    const newTag = "T" + (maxTagIndex + 1).toString().padStart(2, "0");
    let newFunctionChannel = new ButtonChannel({ tag: newTag, parent: panel });
    panel.buttonChannels.add(newFunctionChannel);

    if (obj.isConsumer) {
      newFunctionChannel.channelType = obj.channelType;
      (obj as Consumer).connectWithButtonChannel(newFunctionChannel);
    } else if (obj.isMultiplexer) {
      newFunctionChannel.channelType = obj.channelType;
      // (obj as Multiplexer).connectWithButtonChannel(newFunctionChannel);
    } else if (obj.isScene) {
      newFunctionChannel.channelType = ChannelType.Scene;
      // (obj as Scene).connectWithButtonChannel(newFunctionChannel);
    }

    return newFunctionChannel;
  }

  function handleDrop(obj: Consumer | Multiplexer | Scene) {
    if (obj) {
      //
      // the Consumer is not yet connected to a function of the panel
      //

      let functionChannel1 = functionChannel;
      if (!functionChannel1) {
        functionChannel1 = getFunctionChannel(obj);
      }

      //
      // connect the Function with the Consumer,
      // if not already connected
      //

      let connectionResult: CreateConnectionResult | undefined;
      if (functionChannel1) {
        if (!functionChannel1.isConnectedWith(obj)) {
          connectionResult = Connection.createConnection(functionChannel1, obj);
          if (!connectionResult.connection) {
            showErrToast(
              connectionResult.msg || itx("OnePanelCell.verbindungIstNicht", "Verbindung ist nicht erlaubt"),
            );
            return;
          }
        }
      }

      if (dragStartCell) {
        const lastDropCellState = panelCells.cells.findLast(
          (c) => c.page === pageIndex && c.column === column && c.row === row,
        );
        const replaceCell: PanelCell = {
          functionTag: lastDropCellState?.functionTag || "",
          page: dragStartCell.page,
          column: dragStartCell.column,
          row: dragStartCell.row,
          idx: dragStartCell.idx,
          connectedObjectID: lastDropCellState?.connectedObjectID || "",
        };
        PanelUtils.insertCell(panel, replaceCell);
      }

      const newCell: PanelCell = {
        functionTag: functionChannel1?.tag || "",
        page: pageIndex,
        column: idx ? undefined : column - 1,
        row: idx ? undefined : row - 1,
        idx: idx,
        connectedObjectID: obj?.id || "",
      };

      PanelUtils.insertCell(panel, newCell);
      PanelUtils.applyAllFunctionNumberToAllCells(panel, panelMappingConfig, pageIndex);

      redraw();
    }
    dragStartCell = undefined;
  }

  function onDrop(e: any) {
    e.preventDefault();
    var items = e.dataTransfer.items;
    const item = items[0];
    if (item.kind === "string") {
      item.getAsString(function (sData: string) {
        const data = JSON.parse(sData);

        let obj = state.objectRegistry.getObject(data.id) as ConnectableObject;
        if (obj && (obj.isConsumer || obj.isMultiplexer || obj.isScene)) {
          handleDrop(obj as Consumer | Multiplexer | Scene);
        }
      });
    }
    setTimeout(() => {
      (document.activeElement as HTMLElement | null)?.blur();
    }, 100);
  }

  function onKeyDown(e: any, cell: PanelCell | undefined) {
    if (e.target !== e.currentTarget) {
      return;
    }
    e.preventDefault();
    if (e.key == "Delete" && cell) {
      onUnlink(cell);
    }
  }

  function onClick(e: any) {
    e.preventDefault();

    // if (connectedObject) {
    //   state.selectionManager.selectedObject = connectedObject;
    // }

    state.selectionManager.selectedObject = undefined;

    (e.target as HTMLElement).focus();
  }
  function onDragOver(e: any) {
    e.preventDefault();
    console.log("cell dragged over", row, column);
  }
  function onDragLeave(e: any) {
    e.preventDefault();
    console.log("cell dragged leave", row, column);
  }

  function onDragStart(e: any) {
    dragStartCell = cell ? { ...cell } : undefined;

    if (connectedObject) {
      connectedObject.treeViewModel.dragStart(e);
    }
  }

  function onUnlink(cell: PanelCell) {
    cell.connectedObjectID = "";
    PanelUtils.removeCell(panel, cell);
    PanelUtils.applyAllFunctionNumberToAllCells(panel, panelMappingConfig, pageIndex);
    redraw();
  }

  if (!cell || cell.connectedObjectID == "") {
    return (
      <div
        key={`${row}-${column}`}
        className="cell estadaRow"
        onKeyDown={(e) => onKeyDown(e, undefined)}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        tabIndex={1}
        style={{
          gridColumn: `${column} / span ${width}`,
          gridRow: `${row} / span ${height}`,
        }}
      >
        <div
          className="estadaColumn imageAndTag"
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            state.selectionManager.selectedObject = functionChannel;
            (document.activeElement as HTMLElement | null)?.blur();
          }}
        >
          {isUiModeExpert() && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "grey",
                fontSize: "0.7em",
              }}
            >
              {cell?.idx || ""}
            </div>
          )}
        </div>

        <div className="emptyCellContent" style={{ maxWidth: "150px" }}>
          {itx("OnePanelCell.draggeVerbraucherScene", "Dragge Verbraucher, Scene oder Funktion hierher")}
          {/* <br />
          gridColumn: {`${column} / span ${width}`}
          <br />
          gridRow: {`${row} / span ${height}`} */}
        </div>
      </div>
    );
  }

  const url = getChannelTypePicture(functionChannel?.channelType || ChannelType.Unknown);

  const url2 = getChannelTypePicture(connectedObject?.channelType || ChannelType.Unknown);

  let channelTooltipVariant = "warning"; // no itx
  let channelTypeTooltipText = "";
  let isChannelTypeDifferent = connectedObject?.channelType != functionChannel?.channelType;

  if (isChannelTypeDifferent) {
    channelTooltipVariant = "warning"; // no itx
    channelTypeTooltipText = itx(
      "PanelLayout.channelTypeDifferent",
      "Der Kanaltyp der Funktion und des Verbrauchers sind verschieden",
    );
  }

  let functionNumber = "";
  if (functionChannel) {
    const n = parseInt(functionChannel.tag.substring(1));
    if (!isNaN(n)) {
      functionNumber = `F${n}`;
    }
  }

  const extCellProperties = panelMappingConfig.extCellProperties;

  //return <div className="cell">test</div>;
  const extPropertiesElement = (
    <ExtProperties
      title={itx("OnePanelCell.eigenschaftenFrZelle", "Eigenschaften für Zelle")}
      panel={panel}
      panelMappingConfig={panelMappingConfig}
      pageIndex={pageIndex}
      cell={cell}
      extProperties={extCellProperties}
      onClose={() => {
        setExtPropertiesOpen(false);
        cellRef.current?.focus();
      }}
    />
  );

  return (
    <div
      ref={cellRef}
      key={`${row}-${column}-${connectedObject?.id}`}
      className="cell estadaRow"
      onClick={onClick}
      onKeyDown={(e) => onKeyDown(e, cell)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragStart={onDragStart}
      draggable
      tabIndex={1}
      style={{
        flexGrow: "1",
        gridColumn: `${column} / span ${width}`,
        gridRow: `${row} / span ${height}`,
      }}
    >
      <div
        className="estadaColumn imageAndTag"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();

          state.selectionManager.selectedObject = functionChannel;
          (document.activeElement as HTMLElement | null)?.blur();
        }}
      >
        {isUiModeExpert() && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "grey",
              fontSize: "0.7em",
            }}
          >
            {cell.idx}
          </div>
        )}

        {url && <img className={`icon1`} src={url} draggable={false} />}
        {isUiModeExpert() && <div style={{ color: "grey", fontSize: "0.7em" }}>{functionNumber}</div>}
      </div>
      <div className="estadaColumn left" style={{ flexGrow: "1", padding: "10px" }}>
        <div className="estadaRow" style={{ justifyContent: "space-between", width: "100%" }}>
          <ObjectReference
            obj={connectedObject}
            showName="never" // no itx
            showTag={true}
            showFullTag={true}
            allowDrag={false}
            onNavigated={() => {
              (document.activeElement as HTMLElement | null)?.blur();
            }}
          />

          {isChannelTypeDifferent && (
            <div
              className="estadaColumn hideOnCellFocus"
              style={{
                backgroundColor: "yellow",
                padding: "2px",
                borderRadius: "20px",
                width: "32px",
                height: "32px",
                alignItems: "center",
                justifyContent: "center",
                // marginRight: "20px",
              }}
              data-tooltip-id="tv-tooltip"
              data-tooltip-content={channelTypeTooltipText}
              data-tooltip-place="bottom"
              data-tooltip-variant={channelTooltipVariant as VariantType}
            >
              <img className={`icon1`} style={{ width: "15px", height: "15px" }} src={url2} draggable={false} />
            </div>
          )}

          {extPropertiesElement && (
            <div
              style={{ backgroundColor: "orange" }}
              className="button propertiesButton circle orange"
              onClick={(ev) => {
                ev.stopPropagation();
                setExtPropertiesOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faCog} color="grey" />

              {extPropertiesOpen && (
                <div style={{ position: "relative", top: "0", right: "0", width: "0px" }}>{extPropertiesElement}</div>
              )}
            </div>
          )}

          <div
            style={{ backgroundColor: "orange" }}
            className="unlinkButton button circle orange"
            onClick={(ev) => {
              ev.stopPropagation();
              onUnlink(cell);

              redraw();
            }}
          >
            <i className="las la-unlink" />
          </div>
        </div>

        <div className="estadaRow">
          {functionChannel && (
            <EditableText
              style={{ marginLeft: "5px" }}
              obj={functionChannel}
              property="nameOrConnectedNames"
              disableEdit={false}
              tooltipText={itx("OnePanelCell.beschriftungDerTaste", "Beschriftung der Taste")}
            />
          )}
        </div>
      </div>
    </div>
  );
}
